import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { AbielVoice, LanguageMode } from './types/audioTypes';
import { AudioErrorHandler } from '@/utils/audioErrorHandler';
import { safeAudioProgress, num } from '@/utils/safeNumbers';
import { logger, criticalLogger } from '@/utils/logger';

interface AbielAudioContextType {
  // Audio state
  isPlaying: boolean;
  currentSessionId: string | null;
  isLoading: boolean;
  volume: number;
  progress: number;
  currentTime: number;
  duration: number;
  playbackRate: number;
  controlsVisible: boolean;
  currentLanguage: LanguageMode;
  canAutoplay: boolean;
  isMuted: boolean;
  hasError: boolean;
  error: string | null;
  hasLocalAudioControls: boolean;
  
  // Live stream state
  isLive: boolean;
  
  // Audio controls
  play: (url: string, sessionId: string) => Promise<void>;
  pause: () => void;
  stop: () => void;
  resume: () => void;
  setVolume: (volume: number) => void;
  setPlaybackRate: (rate: number) => void;
  toggleMute: () => void;
  
  // Live stream controls
  attachLiveStream: (stream: MediaStream) => void;
  detachLiveStream: () => void;
  
  // UI controls
  showControls: () => void;
  hideControls: () => void;
  revealControls: () => void;
  
  // Language controls
  setLanguage: (language: LanguageMode) => void;
  
  // Audio control registration
  registerAudioControl: (id: string) => void;
  unregisterAudioControl: (id: string) => void;
}

const AbielAudioContext = createContext<AbielAudioContextType | undefined>(undefined);

export function AbielAudioProvider({ children }: { children: React.ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState<LanguageMode>('english');
  const [canAutoplay, setCanAutoplay] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredControls, setRegisteredControls] = useState<Set<string>>(new Set());
  const [isLive, setIsLive] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const liveAudioRef = useRef<HTMLAudioElement | null>(null);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideControlsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousVolume = useRef<number>(0.8);
  const currentSessionRef = useRef<string | null>(null);

  // Computed properties
  const hasLocalAudioControls = registeredControls.size > 0;

  // Check autoplay capability on mount
  useEffect(() => {
    const checkAutoplay = async () => {
      try {
        const audio = new Audio();
        audio.muted = true;
        audio.volume = 0;
        
        // Try to play a silent audio to test autoplay
        const playPromise = audio.play();
        if (playPromise) {
          await playPromise;
          audio.pause();
          setCanAutoplay(true);
          console.log('[AudioContext] Autoplay is supported');
        }
      } catch (error) {
        console.log('[AudioContext] Autoplay is blocked:', error);
        setCanAutoplay(false);
      }
    };
    
    checkAutoplay();
  }, []);

  const clearError = useCallback(() => {
    setHasError(false);
    setError(null);
  }, []);

  const setAudioError = useCallback((errorMessage: string) => {
    setHasError(true);
    setError(errorMessage);
    criticalLogger.error('[AudioContext] Audio error:', errorMessage);
  }, []);

  // Add auto-hide timer function with updated 10-second duration
  const startAutoHideTimer = useCallback(() => {
    console.log('[AudioContext] ⏰ Starting 10-second auto-hide timer');
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      console.log('[AudioContext] ⏰ Cleared existing auto-hide timer');
    }
    
    autoHideTimerRef.current = setTimeout(() => {
      console.log('[AudioContext] ⏰ AUTO-HIDE TRIGGERED! Setting controlsVisible = false after 10 seconds');
      setControlsVisible(false);
      console.log('[AudioContext] ⏰ Controls hidden, floating button should now appear');
    }, 10000); // Changed from 6000 to 10000 milliseconds
  }, []);

  // Enhanced cleanup with session awareness
  const cleanupAudioElement = useCallback((preserveForTransition: boolean = false) => {
    console.log('[AudioContext] Cleaning up audio element, preserveForTransition:', preserveForTransition);
    
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
      
      // Only fully reset if not preserving for transition
      if (!preserveForTransition) {
        audioRef.current.src = '';
        audioRef.current.load();
        audioRef.current = null;
        currentSessionRef.current = null;
        setCurrentSessionId(null);
      }
    }
    
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(false);
    clearError();
  }, [clearError]);

  // Session-aware reset for transitions
  const resetForNewSession = useCallback((newSessionId: string) => {
    console.log('[AudioContext] Resetting for new session:', newSessionId, 'Previous:', currentSessionRef.current);
    
    // Clean up previous session
    cleanupAudioElement(false);
    
    // Set new session
    currentSessionRef.current = newSessionId;
    setCurrentSessionId(newSessionId);
    
    console.log('[AudioContext] Session reset complete for:', newSessionId);
  }, [cleanupAudioElement]);

  const startProgressTracking = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }
    
    progressTimerRef.current = setInterval(() => {
      if (audioRef.current) {
        const current = num(audioRef.current.currentTime, 0);
        const total = num(audioRef.current.duration, 0);
        
        // Safe audio time values to prevent NaN CoreGraphics errors
        const safeCurrent = Math.max(0, current);
        const safeTotal = Math.max(0.001, total); // Prevent division by zero
        
        setCurrentTime(safeCurrent);
        setDuration(safeTotal);
        
        // Use safe audio progress calculation
        const safeProgress = safeAudioProgress(safeCurrent, safeTotal);
        setProgress(safeProgress * 100);
      }
    }, 1000);
  }, []);

  const play = useCallback(async (url: string, sessionId: string): Promise<void> => {
    console.log('[AudioContext] Play called with:', { 
      url: url.substring(0, 50) + '...', 
      sessionId,
      currentSession: currentSessionRef.current,
      isTransition: currentSessionRef.current !== null
    });
    
    return new Promise((resolve, reject) => {
      try {
        setIsLoading(true);
        clearError();
        
        // Check if this is a session transition
        const isSessionTransition = currentSessionRef.current !== null && currentSessionRef.current !== sessionId;
        
        if (isSessionTransition) {
          console.log('[AudioContext] Session transition detected:', currentSessionRef.current, '->', sessionId);
          // Allow brief pause for cleanup
          setTimeout(() => {
            resetForNewSession(sessionId);
            createAndPlayAudio();
          }, 100);
        } else {
          resetForNewSession(sessionId);
          createAndPlayAudio();
        }
        
        function createAndPlayAudio() {
          // Create new audio element
          const audio = new Audio();
          audioRef.current = audio;
          
          // Set audio properties
          audio.volume = isMuted ? 0 : volume;
          audio.playbackRate = playbackRate;
          audio.crossOrigin = 'anonymous';
          
          // Set up event listeners
          audio.onloadstart = () => {
            console.log('[AudioContext] Audio loading started for:', sessionId);
          };
          
          audio.oncanplay = () => {
            console.log('[AudioContext] Audio can play for:', sessionId);
            setIsLoading(false);
          };
          
          audio.onplay = () => {
            console.log('[AudioContext] 🎵 Audio playback started for:', sessionId);
            setIsPlaying(true);
            setIsLoading(false); // Ensure loading is cleared when playing
            startProgressTracking();
            // Show controls initially, then start 10-second auto-hide timer
            console.log('[AudioContext] 🎵 Setting controlsVisible = true and starting auto-hide timer');
            setControlsVisible(true);
            startAutoHideTimer();
          };
          
          audio.onpause = () => {
            console.log('[AudioContext] ⏸️ Audio paused for:', sessionId);
            setIsPlaying(false);
            setIsLoading(false); // Clear loading state when paused so play button shows
            // Keep controls visible when paused - don't hide automatically
            console.log('[AudioContext] ⏸️ Setting controlsVisible = true (paused state)');
            setControlsVisible(true);
            // Clear auto-hide timer when paused so controls stay visible
            if (autoHideTimerRef.current) {
              clearTimeout(autoHideTimerRef.current);
              autoHideTimerRef.current = null;
              console.log('[AudioContext] ⏸️ Cleared auto-hide timer (paused)');
            }
          };
          
          audio.onended = () => {
            console.log('[AudioContext] Audio playback ended for:', sessionId);
            cleanupAudioElement(false);
            
            // Clean up blob URL
            if (url.startsWith('blob:')) {
              URL.revokeObjectURL(url);
            }
            
            resolve();
          };
          
          audio.onerror = (error) => {
            console.error('[AudioContext] Audio error for:', sessionId, error);
            const audioError = AudioErrorHandler.categorizeError(error);
            AudioErrorHandler.logError(audioError, sessionId);
            setAudioError(`Audio playback failed: ${audioError.message}`);
            
            cleanupAudioElement(false);
            setIsLoading(false);
            
            // Clean up blob URL
            if (url.startsWith('blob:')) {
              URL.revokeObjectURL(url);
            }
            
            reject(new Error(`Audio playback failed: ${audioError.message}`));
          };
          
          // Set source and attempt to play
          audio.src = url;
          audio.load();
          
          // Try to play
          const playPromise = audio.play();
          
          if (playPromise) {
            playPromise.catch((playError) => {
              criticalLogger.error('[AudioContext] Play promise rejected for:', sessionId, playError);
              
              // Handle autoplay restrictions
              if (playError.name === 'NotAllowedError') {
                logger.log('[AudioContext] Autoplay blocked, user interaction required');
                setIsLoading(false);
                
                // For autoplay restrictions, set up user interaction handler
                const handleUserInteraction = () => {
                  logger.log('[AudioContext] User interaction detected, attempting play');
                  audio.play().then(() => {
                    document.removeEventListener('click', handleUserInteraction);
                    document.removeEventListener('touchstart', handleUserInteraction);
                  }).catch(reject);
                };
                
                document.addEventListener('click', handleUserInteraction, { once: true });
                document.addEventListener('touchstart', handleUserInteraction, { once: true });
                
                return; // Don't reject, wait for user interaction
              }
              
              // For other errors, reject
              const audioError = AudioErrorHandler.categorizeError(playError);
              AudioErrorHandler.logError(audioError, sessionId);
              setAudioError(`Audio play failed: ${audioError.message}`);
              
              cleanupAudioElement(false);
              setIsLoading(false);
              
              if (url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
              }
              
              reject(new Error(`Audio play failed: ${audioError.message}`));
            });
          }
        }
        
      } catch (error) {
        console.error('[AudioContext] Play setup error:', error);
        setAudioError('Failed to setup audio playback');
        setIsLoading(false);
        cleanupAudioElement(false);
        reject(error);
      }
    });
  }, [volume, playbackRate, cleanupAudioElement, startProgressTracking, isMuted, clearError, setAudioError, resetForNewSession, startAutoHideTimer]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsLoading(false); // Clear loading state when manually paused
      // Keep controls visible when manually paused
      setControlsVisible(true);
    }
  }, []);

  const stop = useCallback(() => {
    console.log('[AudioContext] Stop called');
    cleanupAudioElement(false);
    setCurrentSessionId(null);
  }, [cleanupAudioElement]);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      logger.log('[AudioContext] Resume called');
      audioRef.current.play().catch((error) => {
        criticalLogger.error('[AudioContext] Resume failed:', error);
        setAudioError('Failed to resume audio playback');
      });
    }
  }, [setAudioError]);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      // Unmute: restore previous volume
      setIsMuted(false);
      setVolume(previousVolume.current);
      if (audioRef.current) {
        audioRef.current.volume = previousVolume.current;
      }
    } else {
      // Mute: save current volume and set to 0
      previousVolume.current = volume;
      setIsMuted(true);
      setVolume(0);
      if (audioRef.current) {
        audioRef.current.volume = 0;
      }
    }
  }, [isMuted, volume]);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    // If setting volume > 0, unmute
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    // Clear auto-hide timer when manually showing controls
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, []);

  const hideControls = useCallback(() => {
    // Only hide if not currently paused
    if (!isPlaying && currentSessionId) {
      // If paused, keep controls visible
      return;
    }
    setControlsVisible(false);
  }, [isPlaying, currentSessionId]);

  const revealControls = useCallback(() => {
    showControls();
    // Auto-hide after 3 seconds for user interactions, but not if paused
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    
    // Only set hide timer if currently playing
    if (isPlaying) {
      hideControlsTimerRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }
  }, [showControls, isPlaying]);

  const setLanguage = useCallback((language: LanguageMode) => {
    console.log('[AudioContext] Language changed to:', language);
    setCurrentLanguage(language);
  }, []);

  const attachLiveStream = useCallback((stream: MediaStream) => {
    console.log('[AudioContext] Attaching live stream');
    
    if (!liveAudioRef.current) {
      const audio = new Audio();
      audio.autoplay = true;
      // Set playsInline using setAttribute for iOS compatibility
      audio.setAttribute('playsinline', '');
      audio.volume = isMuted ? 0 : volume;
      liveAudioRef.current = audio;
      
      // Ensure audio element is attached to DOM for iOS compatibility
      audio.style.display = 'none';
      document.body.appendChild(audio);
    }
    
    liveAudioRef.current.srcObject = stream;
    
    // Explicitly call play() for live streams to ensure playback starts
    liveAudioRef.current.play().catch((error) => {
      console.warn('[AudioContext] Live audio autoplay failed:', error);
      // For iOS/Safari autoplay restrictions, the RealtimeChatInterface
      // will handle user interaction to start playback
    });
    
    setIsLive(true);
    
    // Show controls for live session
    setControlsVisible(true);
    
    console.log('[AudioContext] Live stream attached and playback initiated');
  }, [isMuted, volume]);

  const detachLiveStream = useCallback(() => {
    console.log('[AudioContext] Detaching live stream');
    
    if (liveAudioRef.current) {
      const stream = liveAudioRef.current.srcObject as MediaStream | null;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      liveAudioRef.current.srcObject = null;
      
      // Remove from DOM if we added it
      if (liveAudioRef.current.parentNode) {
        liveAudioRef.current.parentNode.removeChild(liveAudioRef.current);
      }
      
      liveAudioRef.current = null;
    }
    
    setIsLive(false);
  }, []);

  const registerAudioControl = useCallback((id: string) => {
    console.log('[AudioContext] Registering audio control:', id);
    setRegisteredControls(prev => new Set([...prev, id]));
  }, []);

  const unregisterAudioControl = useCallback((id: string) => {
    console.log('[AudioContext] Unregistering audio control:', id);
    setRegisteredControls(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const contextValue: AbielAudioContextType = {
    isPlaying,
    currentSessionId,
    isLoading,
    volume,
    progress,
    currentTime,
    duration,
    playbackRate,
    controlsVisible,
    currentLanguage,
    canAutoplay,
    isMuted,
    hasError,
    error,
    hasLocalAudioControls,
    isLive,
    play,
    pause,
    stop,
    resume,
    setVolume: handleVolumeChange,
    setPlaybackRate,
    toggleMute,
    attachLiveStream,
    detachLiveStream,
    showControls,
    hideControls,
    revealControls,
    setLanguage,
    registerAudioControl,
    unregisterAudioControl,
  };

  return (
    <AbielAudioContext.Provider value={contextValue}>
      {children}
    </AbielAudioContext.Provider>
  );
}

export function useAbielAudio() {
  const context = useContext(AbielAudioContext);
  if (context === undefined) {
    throw new Error('useAbielAudio must be used within an AbielAudioProvider');
  }
  return context;
}
